import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { SharedModule } from "../../../shared/shared.module";
import { ChangeLogService } from "./services/change-log.service";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { ChangeLogModel, ChangeLogSearchModel, ChangeLogSearchResultModel, ProjectModel, SortModel } from "../../../../hub_schema/hubTypes";
import { CommonModule } from "@angular/common";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Subscription, take } from "rxjs";

@Component({
    selector: "app-change-log-dialog",
    imports: [MatFormFieldModule, MatSelectModule, CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
    templateUrl: "./change-log-dialog.component.html",
    styleUrl: "./change-log-dialog.component.scss",
})
export class ChangeLogDialogComponent implements OnInit, OnDestroy {
    private changeLogService = inject(ChangeLogService);
    private dialogRef: MatDialogRef<ChangeLogDialogComponent> = inject(MatDialogRef<ChangeLogDialogComponent>);
    private dialogData: any = inject(MAT_DIALOG_DATA);

    public project: ProjectModel;
    public hubComponents: string[];
    public changeLogEntries: ChangeLogModel[];
    public searchResult: ChangeLogSearchResultModel;
    public users: any[];
    public filterFormChangedSubscription: Subscription;
    private currentPage: number = 0;

    private sortState: SortModel = {
        active: "actionDate",
        direction: "desc",
    };

    public dateRanges = [
        { days: 30, name: "Past 30 Days" },
        { days: 60, name: "Past 60 Days" },
        { days: 90, name: "Past 90 Days" },
        { days: -1, name: "All Time" },
    ];

    public filterForm: FormGroup = new FormGroup({
        dateRangeFilter: new FormControl(this.dateRanges[3]),
        userFilter: new FormControl([]),
        hubComponentFilter: new FormControl([]),
    });

    public ngOnInit(): void {
        this.project = this.dialogData.project;

        this.changeLogService
            .getHubComponents()
            .pipe(take(1))
            .subscribe((components) => {
                this.hubComponents = components;
            });

        this.changeLogService
            .getUsers(this.project.projectId!)
            .pipe(take(1))
            .subscribe((users) => {
                this.users = users;
            });

        this.populateChangeLog();

        this.filterFormChangedSubscription = this.filterForm.valueChanges.subscribe((formValue) => {
            this.currentPage = 0;
            this.populateChangeLog();
        });
    }

    private populateChangeLog(): void {
        const filterFormValue = this.filterForm.value;

        const searchModel: ChangeLogSearchModel = {
            projectId: this.project.projectId!,
            users: filterFormValue.userFilter,
            days: filterFormValue.dateRangeFilter?.days || null,
            components: filterFormValue.hubComponentFilter,
            sort: this.sortState,
            page: this.currentPage,
        };

        this.changeLogService
            .searchChangeLog(searchModel)
            .pipe(take(1))
            .subscribe((result) => {
                this.searchResult = result;
                this.changeLogEntries = result.results;
            });
    }

    public pageRequested(page): void {
        this.currentPage = page;
        this.populateChangeLog();
    }

    public resetFilters(): void {
        this.filterForm.reset({
            dateRangeFilter: this.dateRanges[3],
            userFilter: [],
            hubComponentFilter: [],
        });
        this.currentPage = 0;
    }

    public sortData(sortState: SortModel): void {
        this.sortState = sortState;
        this.currentPage = 0;
        this.populateChangeLog();
    }

    public close(): void {
        this.dialogRef.close();
    }

    public ngOnDestroy(): void {
        if (this.filterFormChangedSubscription) {
            this.filterFormChangedSubscription.unsubscribe();
        }
    }
}
